// src/components/Landing.js
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import {
  MapPin,
  Search,
  CheckCircle,
  Shield,
  Clock,
  Download,
} from "lucide-react";

import { serviceCategories as categories } from "../data/mockData";

// Arabic display names for the top-level categories (keyed by id).
// Falls back to the English name from mockData if an id is missing.
const CATEGORY_AR = {
  plumbing: "السباكة",
  electrical: "الكهرباء",
  carpentry: "النجارة",
  painting: "الدهان",
  "ac-cooling": "التكييف والتبريد",
  handyman: "الصيانة العامة",
  securitySystems:"أنظمة الأمن",
  satelliteTv: "التلفاز و الستلايت",

};

const CONTENT = {
  ar: {
    heroTitle: "اعثر على مقدّمي خدمة موثوقين بالقرب منك",
    heroDesc:
      "تواصل مع مقدّمي خدمة موثوقين لكل احتياجاتك المنزلية — من السباكة والكهرباء إلى النجارة والتنظيف، نغطّيها جميعاً.",
    downloadCta: "حمّل التطبيق",
    servicesTitle: "تصفّح الخدمات",
    servicesDesc: "اختر من مجموعة واسعة من الخدمات الاحترافية",
    howTitle: "كيف تعمل",
    howDesc: "احصل على خدمتك في 4 خطوات بسيطة",
    steps: [
      ["تحميل", "حمّل تطبيق ترستفيكس على هاتفك"],
      ["اختر الخدمة", "اختر الفئة التي تحتاجها"],
      ["التأكيد والدفع", "أكّد حجزك وادفع رسوم المنصة"],
      ["يصلك مقدّم الخدمة", "يُرسَل الطلب إلى أقرب مقدّم خدمة متاح إليك"],
    ],
    trust: [
      ["مقدّمو خدمة موثوقون", "جميع مقدّمي الخدمة تم التحقق منهم وفحص خلفياتهم"],
      ["إسناد فوري", "يذهب طلبك مباشرةً إلى أقرب مقدّم خدمة متاح"],
      ["دفع آمن", "تتم معالجة المدفوعات بأمان عبر ميسّر"],
    ],
    downloadTitle: "حمّل ترستفيكس",
    downloadDesc:
      "احصل على التطبيق لطلب الخدمة من أقرب مقدّم خدمة موثوق — متاح الآن على App Store.",
    appStoreCta: "التحميل من App Store",
    scan: "امسح للتحميل",
  },
  en: {
    heroTitle: "Find Trusted Service Professionals Near You",
    heroDesc:
      "Connect with verified taskers for all your home service needs. From plumbing to electrical, carpentry to cleaning - we've got you covered.",
    downloadCta: "Download the App",
    servicesTitle: "Browse Services",
    servicesDesc: "Choose from a wide range of professional services",
    howTitle: "How It Works",
    howDesc: "Get your service in 4 simple steps",
    steps: [
      ["Download", "Get the TrustFix app on your phone"],
      ["Pick a Service", "Choose the category you need"],
      ["Confirm & Pay", "Confirm your booking and pay the platform fee"],
      ["Get Dispatched", "The nearest available provider is dispatched to you"],
    ],
    trust: [
      ["Verified Providers", "All providers are background checked and verified"],
      ["Instant Dispatch", "Your request goes straight to the nearest available provider"],
      ["Secure Payment", "Payments processed securely through Moyasar"],
    ],
    downloadTitle: "Download TrustFix",
    downloadDesc:
      "Get the app to request a service from the nearest trusted provider — available now on the App Store.",
    appStoreCta: "Download on the App Store",
    scan: "Scan to download",
  },
};

const STEP_ICONS = [
  <Download size={32} />,
  <Search size={32} />,
  <CheckCircle size={32} />,
  <MapPin size={32} />,
];

const TRUST_ICONS = [
  <Shield size={48} />,
  <Clock size={48} />,
  <Shield size={48} />,
];

export default function Landing() {
  const location = useLocation();

  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem("tf_lang") || "ar";
    } catch {
      return "ar";
    }
  });

  const isAr = lang === "ar";
  const t = CONTENT[lang];

  useEffect(() => {
    try {
      localStorage.setItem("tf_lang", lang);
    } catch {}
  }, [lang]);

  useEffect(() => {
    const id = location.state?.scrollTo;
    if (id) {
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [location.state]);

  const categoryLabel = (c) => (isAr ? CATEGORY_AR[c.id] || c.name : c.name);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header lang={lang} setLang={setLang} />

      <div dir={isAr ? "rtl" : "ltr"}>
        {/* Hero Section */}
        <section className="relative max-w-7xl mx-auto px-4 py-16 md:py-24">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-[#1E3A8A] mb-6">{t.heroTitle}</h1>
              <p className="text-lg text-gray-600 mb-8">{t.heroDesc}</p>
              <a
                href="#download"
                className="inline-flex items-center justify-center gap-2 bg-[#006C35] text-white px-8 py-4 rounded-lg hover:bg-[#005428] transition-colors"
              >
                <Download size={20} />
                {t.downloadCta}
              </a>
            </div>

            <div className="hidden md:block">
              <img
                src="/mockup.png"
                alt="TrustFix app"
                className="w-full object-contain h-100"
              />
            </div>
          </div>
        </section>

        {/* Service Categories */}
        <section id="services" className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-center text-[#1E3A8A] mb-4">
              {t.servicesTitle}
            </h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
              {t.servicesDesc}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              {categories.map((category) => (
                <div
                  key={category.id}
                  className="bg-gray-50 rounded-xl p-6 text-center"
                >
                  <div className="text-4xl mb-3">{category.icon}</div>
                  <h3 className="text-sm">{categoryLabel(category)}</h3>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it Works */}
        <section id="how-it-works" className="py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-center text-[#1E3A8A] mb-4">{t.howTitle}</h2>
            <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
              {t.howDesc}
            </p>
            <div className="grid md:grid-cols-4 gap-8">
              {t.steps.map(([title, desc], i) => (
                <div
                  key={i}
                  className="bg-white rounded-xl p-8 text-center shadow-sm"
                >
                  <div className="w-16 h-16 bg-[#006C35] text-white rounded-full flex items-center justify-center mx-auto mb-4">
                    {STEP_ICONS[i]}
                  </div>
                  <h3 className="text-[#1E3A8A] mb-3">{title}</h3>
                  <p className="text-gray-600">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust Signals */}
        <section className="bg-[#006C35] text-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              {t.trust.map(([title, desc], i) => (
                <div key={i}>
                  <div className="flex justify-center mb-4">{TRUST_ICONS[i]}</div>
                  <h3 className="mb-2">{title}</h3>
                  <p className="opacity-90">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Download Section */}
        <section id="download" className="max-w-7xl mx-auto px-4 py-16">
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-[#1E3A8A] mb-4">{t.downloadTitle}</h2>
                <p className="text-gray-600 mb-6">{t.downloadDesc}</p>
                <a
                  href="https://apps.apple.com/sa/app/trustfix/id6809151329"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#006C35] text-white px-6 py-3 rounded-lg hover:bg-[#005428] transition-colors"
                >
                  <Download size={20} />
                  {t.appStoreCta}
                </a>
              </div>

              <div className="flex justify-center md:justify-end">
                <div className="text-center">
                  <div className="bg-white border border-gray-200 rounded-2xl p-4 inline-block shadow-sm">
                    <img
                      src="/appstore-qr.png"
                      alt="Scan to download TrustFix"
                      className="w-40 h-40 object-contain"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-3">{t.scan}</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer lang={lang} />
    </div>
  );
}
